import React, { useState } from "react";
import { Flame, ShieldCheck, ArrowLeft, Mail, Lock, User, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useNavigate, Link } from "react-router-dom";

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return err?.message || "Something went wrong. Please try again.";
};

const PortalLogin = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState("signin"); // signin | signup | forgot
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/portal/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setMsg(null); setBusy(true);
    try {
      if (tab === "signin") {
        await api.post("/portal/auth/login", { email: form.email, password: form.password });
        nav("/portal");
      } else if (tab === "signup") {
        await api.post("/portal/auth/register", {
          email: form.email, password: form.password, name: form.name,
        });
        nav("/portal");
      } else {
        const r = await api.post("/portal/auth/forgot", { email: form.email });
        setMsg(r.data?.message || "If an account exists, a reset link has been sent.");
      }
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 ember-bg">
      <div className="w-full max-w-md bento-card !p-9" data-testid="portal-login">
        <span className="glow-blob" />
        <div className="text-center">
          <div className="icon-badge !h-14 !w-14 mx-auto mb-5">
            <Flame className="h-7 w-7 text-[#F28C18]" strokeWidth={1.5} />
          </div>
          <div className="overline mb-2">RFMG Member Portal</div>
          <h1 className="font-serif-display text-3xl text-[#F6F0E6]">
            Carry the <span className="italic gold-text">Fire</span>
          </h1>
          <p className="mt-3 text-sm text-[#D9D9D9]/85">
            Submit prayer requests. Access recordings. Partner with the work.
          </p>
        </div>

        {/* Tabs */}
        <div className="mt-7 grid grid-cols-2 gap-1 p-1 bg-white/5 rounded-full border border-[#C9A44C]/15">
          {[["signin","Sign in"],["signup","Sign up"]].map(([key,label]) => (
            <button
              key={key}
              type="button"
              onClick={() => { setTab(key); setError(null); setMsg(null); }}
              className={`py-2 rounded-full text-xs tracking-[0.22em] uppercase transition-all ${tab===key ? "bg-[#C9A44C] text-[#0a0a0a] font-semibold" : "text-[#D9D9D9]/75 hover:text-[#F6F0E6]"}`}
              data-testid={`tab-${key}`}
            >{label}</button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="mt-6 space-y-3" data-testid="portal-form">
          {tab === "signup" && (
            <label className="block">
              <span className="sr-only">Full name</span>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A44C]/70" strokeWidth={1.6} />
                <input
                  type="text" required minLength={2} maxLength={120}
                  value={form.name} onChange={update("name")}
                  placeholder="Full name"
                  className="w-full bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-xl pl-11 pr-4 py-3 text-[15px] text-[#F6F0E6] placeholder-[#D9D9D9]/45 focus:outline-none focus:border-[#C9A44C]/55"
                  data-testid="portal-name"
                />
              </div>
            </label>
          )}
          <label className="block">
            <span className="sr-only">Email address</span>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A44C]/70" strokeWidth={1.6} />
              <input
                type="email" required autoComplete="email"
                value={form.email} onChange={update("email")}
                placeholder="Email address"
                className="w-full bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-xl pl-11 pr-4 py-3 text-[15px] text-[#F6F0E6] placeholder-[#D9D9D9]/45 focus:outline-none focus:border-[#C9A44C]/55"
                data-testid="portal-email"
              />
            </div>
          </label>
          {tab !== "forgot" && (
            <label className="block">
              <span className="sr-only">Password</span>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C9A44C]/70" strokeWidth={1.6} />
                <input
                  type="password" required minLength={8} maxLength={128}
                  autoComplete={tab === "signup" ? "new-password" : "current-password"}
                  value={form.password} onChange={update("password")}
                  placeholder={tab === "signup" ? "Create a password (min 8)" : "Password"}
                  className="w-full bg-[#0a0a0a] border border-[#C9A44C]/25 rounded-xl pl-11 pr-4 py-3 text-[15px] text-[#F6F0E6] placeholder-[#D9D9D9]/45 focus:outline-none focus:border-[#C9A44C]/55"
                  data-testid="portal-password"
                />
              </div>
            </label>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-[#FCA5A5] bg-[#7f1d1d]/20 border border-[#FCA5A5]/30 rounded-xl px-3 py-2.5" data-testid="portal-error">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          {msg && (
            <div className="text-sm text-[#C9A44C] bg-[#C9A44C]/8 border border-[#C9A44C]/30 rounded-xl px-3 py-2.5" data-testid="portal-msg">{msg}</div>
          )}

          <button type="submit" disabled={busy} className="btn-fire w-full" data-testid="portal-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-4 w-4" />}
            {busy ? "One moment…" : (tab === "signin" ? "Sign in" : tab === "signup" ? "Create my account" : "Send reset link")}
          </button>

          {tab === "signin" && (
            <button type="button" onClick={() => { setTab("forgot"); setError(null); setMsg(null); }} className="w-full text-center text-xs text-[#C9A44C]/80 hover:text-[#C9A44C] py-1" data-testid="portal-forgot-link">
              Forgot your password?
            </button>
          )}
          {tab === "forgot" && (
            <button type="button" onClick={() => { setTab("signin"); setError(null); setMsg(null); }} className="w-full text-center text-xs text-[#D9D9D9]/65 hover:text-[#F6F0E6] py-1">
              Back to sign in
            </button>
          )}
        </form>

        {/* Divider + Google */}
        <div className="my-6 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#D9D9D9]/40">
          <span className="flex-1 border-t border-[#C9A44C]/15" />or<span className="flex-1 border-t border-[#C9A44C]/15" />
        </div>
        <button onClick={handleGoogle} className="btn-ghost w-full !py-3" data-testid="portal-google-signin">
          <ShieldCheck className="h-4 w-4" strokeWidth={1.7} />
          Continue with Google
        </button>

        <Link to="/" className="mt-7 flex items-center justify-center gap-1 text-xs text-[#D9D9D9]/65 hover:text-[#C9A44C]" data-testid="portal-back">
          <ArrowLeft className="h-3 w-3" /> Back to public site
        </Link>
      </div>
    </div>
  );
};

export default PortalLogin;
